import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import axios from 'axios';
import { Link, useLocation } from 'react-router-dom';
import {
  BuildingOfficeIcon,
  PhoneIcon,
  EnvelopeIcon,
  UserIcon,
  UserCircleIcon,
  IdentificationIcon,
  ShieldCheckIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/outline';
import { Badge, Button, Card, CardHeader, ContentLoader, Input } from '@/components/common';
// Imported by path: `pages/customers/index.ts` is outside this lane's files.
import { KycDocumentsPanel } from '@/pages/customers/KycDocumentsPanel';
import { formatDate } from '@/utils';
import { digitsOnlyOnChange, nameOnChange } from '@/utils/validation';
// Not this lane's file, but this is the same self-service profile the old
// Settings > Profile tab used (`SettingsPage.tsx`) — reused here rather than
// rebuilt, see SelfServiceProfileCard below.
import { useProfile, useUpdateProfileSettings, useUploadProfilePicture } from '@/hooks/useSettings';
import { downscaleImage, isTooLarge } from '@/utils/imageDownscale';
import { getErrorMessage } from '@/services/api';
import { toast } from 'react-hot-toast';
import { ResetPasswordCard } from './ResetPasswordCard';
import { TelegramConnectCard } from './TelegramConnectCard';
import { CustomerAdvisors } from './CustomerAdvisors';
import { MyPoliciesCard } from './MyPoliciesCard';
import {
  ContactRow,
  KYC_STAGE,
  ProfilePhotoSection,
  customerAddress,
  customerDisplayName,
  useCustomerMe,
} from './portalProfile';

const selfServiceProfileSchema = z.object({
  first_name: z.string().min(1, 'First name is required'),
  last_name: z.string().min(1, 'Last name is required'),
  phone: z.string().optional(),
});

type SelfServiceProfileFormData = z.infer<typeof selfServiceProfileSchema>;

/**
 * C1 follow-up (review finding, medium) — a customer login with NO linked
 * `customers.Customer` row (3 of 26 in prod) has no "My Profile" card and no
 * "Edit my details" link above, because both are gated on `hasProfile`. Before
 * C1 removed Settings > Profile, that generic tab (writing to `users.User` via
 * `/users/me/`) was reachable by every role including this one, so such a
 * customer could still edit their own name, phone and photo. Losing the tab
 * must not also lose that: this card is the same editor, kept alive here
 * instead of dead-ended behind a profile that does not exist.
 */
function SelfServiceProfileCard() {
  const { data: profile, isLoading } = useProfile();
  const updateProfileMutation = useUpdateProfileSettings();
  const uploadPictureMutation = useUploadProfilePicture();

  const { register, handleSubmit, reset, formState: { errors } } = useForm<SelfServiceProfileFormData>({
    resolver: zodResolver(selfServiceProfileSchema),
    defaultValues: { first_name: '', last_name: '', phone: '' },
  });

  useEffect(() => {
    if (profile) {
      reset({
        first_name: profile.first_name,
        last_name: profile.last_name,
        phone: profile.phone || '',
      });
    }
  }, [profile, reset]);

  const onSubmit = (data: SelfServiceProfileFormData) => {
    updateProfileMutation.mutate(data);
  };

  const handlePictureUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    if (isTooLarge(file)) {
      toast.error('That image is larger than 25MB. Please choose a smaller one.');
      return;
    }
    try {
      const prepared = await downscaleImage(file);
      const formData = new FormData();
      formData.append('profile_picture', prepared);
      await uploadPictureMutation.mutateAsync({ formData });
    } catch (error) {
      if (!(error instanceof Error) || !error.message.includes('status code')) {
        toast.error(getErrorMessage(error));
      }
    }
  };

  if (isLoading) return <ContentLoader />;

  return (
    <Card>
      <CardHeader
        title="My Profile"
        subtitle="No customer profile is linked to this login yet, so KYC and policy details aren't available. Contact your broker to link one — you can still update your own name, phone and photo below."
      />
      <div className="flex items-center gap-6 mb-6">
        <div className="w-16 h-16 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center overflow-hidden flex-shrink-0">
          {profile?.profile_picture ? (
            <img src={profile.profile_picture} alt="Profile" className="w-full h-full object-cover" />
          ) : (
            <UserCircleIcon className="w-10 h-10 text-gray-400" />
          )}
        </div>
        <label className="cursor-pointer inline-flex items-center px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors">
          {uploadPictureMutation.isPending ? 'Uploading...' : 'Upload Photo'}
          <input
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handlePictureUpload}
            disabled={uploadPictureMutation.isPending}
          />
        </label>
      </div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <Input
            label="First Name"
            error={errors.first_name?.message}
            required
            {...register('first_name', { onChange: nameOnChange })}
          />
          <Input
            label="Last Name"
            error={errors.last_name?.message}
            required
            {...register('last_name', { onChange: nameOnChange })}
          />
          <Input
            label="Phone"
            inputMode="numeric"
            maxLength={10}
            error={errors.phone?.message}
            {...register('phone', { onChange: digitsOnlyOnChange })}
          />
        </div>
        <div className="mt-6 flex justify-end">
          <Button type="submit" isLoading={updateProfileMutation.isPending}>
            Save Changes
          </Button>
        </div>
      </form>
    </Card>
  );
}

/** The anchor `/my-contacts` (and anything else) links a customer to. */
export const ADVISORS_ANCHOR = 'advisors';

/**
 * C1 / M-01 / U-01 — the customer's Profile section, drawn INSIDE /settings.
 *
 * C1 moved My Profile, KYC Status, My KYC Documents and Reset Password off
 * "My Contacts" onto a separate /settings/my-profile page, linked from a
 * "My Account" group on Settings. M-01 asks for one place instead: this
 * section is the customer's Profile tab on /settings itself, and it also
 * carries My Policies (each with its broker and agent) and the advisor cards
 * that were "My Contacts" (U-01). /settings/my-profile and /my-contacts now
 * redirect here.
 */
export function CustomerProfileSection() {
  const { data, isLoading, error, refetch } = useCustomerMe();
  const { hash } = useLocation();

  // `/my-contacts` lands on `/settings#advisors`. The target only exists once
  // the profile has loaded, so the browser's own hash jump has already missed
  // it; scroll once it is there. (jsdom has no scrollIntoView -- optional call.)
  const advisorsReady = !isLoading && !!data?.customer_code;
  useEffect(() => {
    if (advisorsReady && hash === `#${ADVISORS_ANCHOR}`) {
      document.getElementById(ADVISORS_ANCHOR)?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    }
  }, [advisorsReady, hash]);

  if (isLoading) return <ContentLoader />;

  // `/customers/me/` 404s "Customer profile not found" for a customer login
  // with no linked Customer row (backend apps/customers/views.py `me`) — that
  // is not a transient failure, it is the no-profile case SelfServiceProfileCard
  // exists for below. Anything else (network blip, 5xx) IS a failure and must
  // render as one, never as "you have no profile": the page below would
  // otherwise draw an empty profile and an unanswered KYC stage over data
  // nobody actually read.
  const isNoLinkedProfile = axios.isAxiosError(error) && error.response?.status === 404;
  if (error && !isNoLinkedProfile) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 dark:text-gray-400">Failed to load your profile.</p>
        <button
          type="button"
          onClick={() => refetch()}
          className="mt-3 text-sm font-medium text-primary-600 hover:text-primary-700"
        >
          Try again
        </button>
      </div>
    );
  }

  const hasProfile = !!data?.customer_code;
  const kycStage = data?.kyc_status ? KYC_STAGE[data.kyc_status] : undefined;
  const profileName = customerDisplayName(data);
  const profileAddress = customerAddress(data);

  return (
    <div>
      {!hasProfile && (
        <div className="mb-6">
          <SelfServiceProfileCard />
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Profile Card — CUSTOMER-2B */}
        {hasProfile && (
          <Card>
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center">
                  <IdentificationIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
                <h2 className="text-base font-semibold text-gray-900 dark:text-white">My Profile</h2>
              </div>
              <ProfilePhotoSection profile={data} name={profileName} />
              <div className="divide-y divide-gray-100 dark:divide-gray-700">
                <ContactRow icon={<IdentificationIcon className="w-4 h-4" />} label="Customer Code" value={data?.customer_code} />
                <ContactRow icon={<UserIcon className="w-4 h-4" />} label="Name" value={profileName || undefined} />
                <ContactRow icon={<PhoneIcon className="w-4 h-4" />} label="Phone" value={data?.phone} />
                <ContactRow icon={<EnvelopeIcon className="w-4 h-4" />} label="Email" value={data?.email} />
                <ContactRow
                  icon={<UserIcon className="w-4 h-4" />}
                  label="Date of Birth"
                  value={data?.date_of_birth ? formatDate(data.date_of_birth) : undefined}
                />
                <ContactRow icon={<BuildingOfficeIcon className="w-4 h-4" />} label="Address" value={profileAddress || undefined} />
                <ContactRow
                  icon={<UserIcon className="w-4 h-4" />}
                  label="Customer Since"
                  value={data?.customer_since ? formatDate(data.customer_since) : undefined}
                />
              </div>
              {/* `/settings/profile` is the editable version of this card and
                  has existed, routed and working, with NOTHING linking to it.
                  This is now its way in. */}
              <Link
                to="/settings/profile"
                className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700"
              >
                <PencilSquareIcon className="w-4 h-4" />
                Edit my details
              </Link>
            </div>
          </Card>
        )}

        {/* KYC Status Card — CUSTOMER-2B */}
        {hasProfile && (
          <Card>
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-purple-50 dark:bg-purple-900/20 flex items-center justify-center">
                  <ShieldCheckIcon className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                </div>
                <h2 className="text-base font-semibold text-gray-900 dark:text-white">KYC Status</h2>
              </div>
              <div className="flex items-center justify-between py-2">
                <span className="text-sm text-gray-500">Verification Stage</span>
                <Badge variant={kycStage?.variant ?? 'default'}>
                  {kycStage?.label ?? 'Not started'}
                </Badge>
              </div>
              {data?.kyc_verified_at && (
                <ContactRow
                  icon={<ShieldCheckIcon className="w-4 h-4" />}
                  label="Verified On"
                  value={formatDate(data.kyc_verified_at)}
                />
              )}
              <p className="mt-2 text-xs text-gray-400">
                KYC is verified by your broker. This view is read-only.
              </p>
            </div>
          </Card>
        )}

        {/* KYC documents — the customer's own upload point.
            `canVerify` is false and stays false: a customer may supply
            evidence and confirm the details read off their own card, but the
            verdict on that evidence is their broker's (and their POSP's) to
            make. The server enforces it; this prop only decides whether
            unusable buttons are drawn. */}
        {hasProfile && data?.id && (
          <div className="md:col-span-2">
            <KycDocumentsPanel
              customerId={data.id}
              canVerify={false}
              title="My KYC Documents"
              subtitle="Upload your identity documents. We read the details off them for you to check — nothing is saved until you confirm it."
            />
          </div>
        )}

        {/* M-01: every policy, with the broker and agent it is mapped to. */}
        {hasProfile && (
          <div className="md:col-span-2">
            <MyPoliciesCard />
          </div>
        )}

        {/* U-01 put the broker/agent cards from "My Contacts" here; C2/B-05
            replaced them with the whole tree, because one broker and one agent
            is the wrong answer for a login with policies at two brokers or
            sold by two agents. Its own read (`/customers/me/advisors/`), so it
            carries its own loading, error and empty states. */}
        {hasProfile && (
          <section id={ADVISORS_ANCHOR} aria-labelledby="advisors-heading" className="md:col-span-2 scroll-mt-20">
            <h2 id="advisors-heading" className="text-base font-semibold text-gray-900 dark:text-white">
              Your advisors
            </h2>
            <p className="mb-3 text-sm text-gray-500 dark:text-gray-400">
              Everyone looking after your policies. Tap a name for their phone, email and what they cover.
            </p>
            <CustomerAdvisors />
          </section>
        )}

        {/* "Connect my Telegram": the broker's bot knows them even if their Telegram number differs.
            Needs a Customer record, which is what the server links the chat to. */}
        {hasProfile && <TelegramConnectCard />}

        {/* Reset Password Card — CUSTOMER-2C */}
        <ResetPasswordCard />
      </div>
    </div>
  );
}
